import { api } from 'services/api';
import type {
  BotInfo,
  User,
  SalesSum,
  BotDetailedInfo,
  UserBalanceResponse,
  UsersFilter
} from 'types';
import { GetUserLogsParams, UserLogsResponse } from 'types/userLogs';

const usersApi = api
  .enhanceEndpoints({
    addTagTypes: ['User', 'Users', 'TgUsers', 'MaxUsers', 'UserAuditLogs']
  })
  .injectEndpoints({
    endpoints: ($) => ({
      getAllUsers: $.query<
        { next: string; users: User[] },
        { next?: string; limit: number; filters?: UsersFilter }
      >({
        query: (params) => ({
          url: `/users/admin/query`,
          method: 'post',
          data: params
        }),
        providesTags: ['Users']
      }),
      getUser: $.query<User, string>({
        query: (id: string) => ({
          url: `/users/admin/users/byid/${id}`,
          method: 'get'
        }),
        providesTags: (result) => [{ type: 'User', id: result?.Id }]
      }),
      getBotInfo: $.query<BotInfo, void>({
        query: () => ({
          url: `/users/bot`
        })
      }),
      getMaxBotInfo: $.query<BotInfo, void>({
        query: () => ({
          url: `/users/max`
        })
      }),
      getBotDetailedInfo: $.query<BotDetailedInfo[], string>({
        query: (id: string) => ({
          url: `/users/${id}/bot/details`,
          method: 'get'
        }),
        providesTags: ['TgUsers']
      }),
      removeTgUserEntry: $.mutation<void, { userId: string; chatId: string }>({
        query: ({ userId, chatId }) => ({
          url: `/users/${userId}/bot`,
          method: 'DELETE',
          params: {
            chatId
          }
        }),
        invalidatesTags: ['TgUsers']
      }),
      getMaxDetailedInfo: $.query<BotDetailedInfo[], string>({
        query: (id: string) => ({
          url: `/users/${id}/max/details`,
          method: 'get'
        }),
        providesTags: ['MaxUsers']
      }),
      removeMaxUserEntry: $.mutation<void, { userId: string; chatId: string }>({
        query: ({ userId, chatId }) => ({
          url: `/users/${userId}/max`,
          method: 'DELETE',
          params: {
            chatId
          }
        }),
        invalidatesTags: ['MaxUsers']
      }),
      getUserSales: $.query<
        SalesSum,
        { from: string; to: string; userId: string }
      >({
        query: ({ from, to, userId }) => ({
          url: `${userId}/sales/sum`,
          params: {
            from,
            to
          }
        })
      }),
      createUser: $.mutation<void, User>({
        query: (user) => ({
          url: '/users/admin/users/create',
          method: 'POST',
          data: user
        }),
        invalidatesTags: ['Users']
      }),
      deleteUser: $.mutation<void, string>({
        query: (userName: string) => ({
          url: `/users/admin/users/${userName}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Users']
      }),
      resetPassword: $.mutation({
        query: (userName: string) => ({
          url: `/users/admin/users/${userName}/resetpassword`,
          method: 'POST'
        }),
        invalidatesTags: ['User', 'Users']
      }),
      blockUser: $.mutation({
        query: (userName: string) => ({
          url: `/users/admin/users/${userName}/disable`,
          method: 'POST'
        }),
        invalidatesTags: ['User', 'Users']
      }),
      unblockUser: $.mutation({
        query: (userName: string) => ({
          url: `/users/admin/users/${userName}/enable`,
          method: 'POST'
        }),
        invalidatesTags: ['User', 'Users']
      }),
      changePassword: $.mutation({
        query: (data: { PreviousPassword: string; NewPassword: string }) => ({
          url: '/users/auth/changepassword',
          method: 'POST',
          data
        })
      }),

      updateUser: $.mutation({
        query: ({
          Username,
          Groups,
          Role,
          Phone,
          Email,
          Emails,
          Name,
          GivenName,
          FamilyName,
          Company,
          Bin,
          Comment
        }: User) => ({
          url: `/users/${Username}/profile`,
          method: 'POST',
          data: {
            Groups,
            Username,
            Role,
            Phone,
            Email,
            Emails,
            Name,
            GivenName,
            FamilyName,
            Company,
            Bin,
            Comment
          }
        }),
        invalidatesTags: (_res, _err, data) => [{ type: 'User', id: data.Id }]
      }),
      loyaltyBalance: $.query<UserBalanceResponse, string>({
        query: (email) => ({
          url: 'https://alivewater.ru/api/getBonuses.php',
          method: 'GET',
          params: {
            email,
            key: 'cGeh48qQpatB5Brhgk57PMKhK4CdE9qL'
          }
        })
      }),
      userLogs: $.query<UserLogsResponse, GetUserLogsParams>({
        query: ({ userId, ...params }) => ({
          url: `/users/${userId}/logs`,
          method: 'GET',
          params
        })
      })
    })
  });

export const {
  useGetUserQuery,
  useCreateUserMutation,
  useGetAllUsersQuery,
  useGetBotInfoQuery,
  useGetMaxBotInfoQuery,
  useResetPasswordMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useDeleteUserMutation,
  useGetUserSalesQuery,
  useChangePasswordMutation,
  useUpdateUserMutation,
  useLazyGetUserQuery,
  useGetBotDetailedInfoQuery,
  useRemoveTgUserEntryMutation,
  useGetMaxDetailedInfoQuery,
  useRemoveMaxUserEntryMutation,
  useLoyaltyBalanceQuery,
  useUserLogsQuery
} = usersApi;
